import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { 
  ordersApi, 
  type Order, 
  type OrdersListResponse, 
  type OrdersQueryParams,
  type CreateOrderRequest 
} from '../../services/ordersService'

// Async thunks
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async ({ ordersHref, params }: { ordersHref: string; params?: OrdersQueryParams }) => {
    console.log('[ordersSlice] fetchOrders thunk called', { ordersHref, params });
    const response = await ordersApi.getOrders(ordersHref, params);
    return response;
  }
)

export const fetchOrder = createAsyncThunk(
  'orders/fetchOrder',
  async (orderHref: string) => {
    console.log('[ordersSlice] fetchOrder thunk called', { orderHref });
    const response = await ordersApi.getOrder(orderHref)
    return response
  }
)

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async ({ ordersHref, orderData }: { ordersHref: string; orderData: CreateOrderRequest }) => {
    console.log('[ordersSlice] createOrder thunk called', { ordersHref, orderData });
    const response = await ordersApi.createOrder(ordersHref, orderData)
    return response
  }
)

export const searchOrders = createAsyncThunk(
  'orders/searchOrders',
  async ({ ordersHref, searchTerm }: { ordersHref: string; searchTerm: string }) => {
    console.log('[ordersSlice] searchOrders thunk called', { ordersHref, searchTerm });
    const response = await ordersApi.getOrders(ordersHref, { search: [searchTerm] });
    return response;
  }
)

// Fetch orders for a specific page using HATEOAS URLs or page number
export const fetchOrdersPage = createAsyncThunk(
  'orders/fetchOrdersPage',
  async (params: { page?: number; hateoasUrl?: string }, { getState }) => {
    const state = getState() as any
    
    let url: string
    let queryParams: OrdersQueryParams = {}
    
    if (params.hateoasUrl) {
      // Use the HATEOAS URL, but replace {cid} placeholder with actual clinic ID
      url = params.hateoasUrl
      
      // Fix backend bug: Replace {cid} placeholder with actual clinic ID
      const clinicId = state.auth.currentClinic?.id
      if (clinicId && url.includes('{cid}')) {
        url = url.replace('{cid}', clinicId)
        console.log('HATEOAS: Replaced {cid} placeholder with clinic ID:', clinicId)
      }
      
      console.log('HATEOAS: Following orders pagination URL:', url)
    } else if (params.page) {
      // Use page number with base orders href
      const ordersHref = state.auth.currentClinic?.orders?.href
      
      if (!ordersHref) {
        throw new Error('No clinic selected or clinic missing orders link')
      }
      
      url = ordersHref
      queryParams = { 
        page_number: params.page,
        page_size: state.orders.pageSize 
      }
      
      console.log('HATEOAS: Fetching orders page', params.page, 'with params:', queryParams)
    } else {
      throw new Error('Either page number or HATEOAS URL must be provided')
    }
    
    const response = await ordersApi.getOrders(url, queryParams)
    return response
  }
)

// State interface
interface OrdersState {
  orders: Order[]
  currentOrder: Order | null
  loading: boolean
  isSearching: boolean
  error: string | null
  searchTerm: string
  selectedStatusFilter: string[]
  selectedPatientFilter: string
  selectedPharmacyFilter: string
  total: number
  pageSize: number
  pageNumber: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// Initial state
const initialState: OrdersState = {
  orders: [],
  currentOrder: null,
  loading: false,
  isSearching: false,
  error: null,
  searchTerm: '',
  selectedStatusFilter: [],
  selectedPatientFilter: '',
  selectedPharmacyFilter: '',
  total: 0,
  pageSize: 10,
  pageNumber: 1,
  hasNextPage: false,
  hasPreviousPage: false,
}

// Slice
const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSelectedStatusFilter: (state, action: PayloadAction<string[]>) => {
      state.selectedStatusFilter = action.payload;
    },
    setSelectedPatientFilter: (state, action: PayloadAction<string>) => {
      state.selectedPatientFilter = action.payload;
    },
    setSelectedPharmacyFilter: (state, action: PayloadAction<string>) => {
      state.selectedPharmacyFilter = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.total = 0;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOrders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action: PayloadAction<OrdersListResponse>) => {
        state.loading = false;
        state.orders = action.payload.items;
        state.total = action.payload.total_items;
        state.pageSize = action.payload.page_size;
        state.pageNumber = action.payload.page_number;
        state.hasNextPage = action.payload.page_next !== null;
        state.hasPreviousPage = action.payload.page_previous !== null;
        state.error = null;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch orders';
        console.error('[ordersSlice] fetchOrders failed:', action.error);
      })

      // fetchOrder
      .addCase(fetchOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrder.fulfilled, (state, action: PayloadAction<Order>) => {
        state.loading = false;
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch order';
        console.error('[ordersSlice] fetchOrder failed:', action.error);
      })
      // createOrder
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action: PayloadAction<Order>) => {
        state.loading = false;
        // Add new order to the beginning of the list
        state.orders.unshift(action.payload);
        state.total += 1;
        state.error = null;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create order';
        console.error('[ordersSlice] createOrder failed:', action.error);
      })
      // searchOrders
      .addCase(searchOrders.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(searchOrders.fulfilled, (state, action: PayloadAction<OrdersListResponse>) => {
        state.isSearching = false;
        state.orders = action.payload.items;
        state.total = action.payload.total_items;
        state.pageSize = action.payload.page_size;
        state.pageNumber = action.payload.page_number;
        state.hasNextPage = action.payload.page_next !== null;
        state.hasPreviousPage = action.payload.page_previous !== null;
        state.error = null;
      })
      .addCase(searchOrders.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.error.message || 'Failed to search orders';
        console.error('[ordersSlice] searchOrders failed:', action.error);
      });
  },
})

// Export actions
export const { 
  setSearchTerm,
  setSelectedStatusFilter,
  setSelectedPatientFilter,
  setSelectedPharmacyFilter,
  clearError,
  clearOrders,
  clearCurrentOrder 
} = ordersSlice.actions

// Selectors
export const selectOrders = (state: { orders: OrdersState }) => state.orders.orders
export const selectCurrentOrder = (state: { orders: OrdersState }) => state.orders.currentOrder
export const selectOrdersLoading = (state: { orders: OrdersState }) => state.orders.loading
export const selectOrdersSearching = (state: { orders: OrdersState }) => state.orders.isSearching
export const selectOrdersError = (state: { orders: OrdersState }) => state.orders.error
export const selectSearchTerm = (state: { orders: OrdersState }) => state.orders.searchTerm
export const selectSelectedStatusFilter = (state: { orders: OrdersState }) => state.orders.selectedStatusFilter
export const selectSelectedPatientFilter = (state: { orders: OrdersState }) => state.orders.selectedPatientFilter
export const selectSelectedPharmacyFilter = (state: { orders: OrdersState }) => state.orders.selectedPharmacyFilter
export const selectOrdersTotal = (state: { orders: OrdersState }) => state.orders.total
export const selectOrdersPagination = (state: { orders: OrdersState }) => ({
  pageSize: state.orders.pageSize,
  pageNumber: state.orders.pageNumber,
  hasNextPage: state.orders.hasNextPage,
  hasPreviousPage: state.orders.hasPreviousPage,
  total: state.orders.total
})

export default ordersSlice.reducer